<template>
    <main class="layout__main--narrow layout__main--no-top-padding">
        <BaseHeader
            v-if="pending || headerImage"
            :placeholder="pending"
            :image="headerImage"
        />

        <BaseSection>
            <BaseBackButton
                v-if="page && page.parent && page.parent.uri"
                :to="`/${page.parent.uri}`"
            >
                {{ page.parent.title }}
            </BaseBackButton>
            <BaseBackButton
                v-else
                to="/about/how-we-work"
            >
                Offices
            </BaseBackButton>
        </BaseSection>

        <transition name="fade">
            <article
                v-if="!pending && page"
                class="bleed"
            >
                <BaseSection class="container-narrow">
                    <template #title>
                        <h1>{{ page.title }}</h1>
                    </template>

                    <!-- eslint-disable vue/no-v-html -->
                    <div
                        v-if="page.introText"
                        class="lead"
                        data-allow-anchor
                        v-html="page.introText"
                    />
                    <!-- eslint-enable vue/no-v-html -->

                    <template v-if="page.contactEmail">
                        <BaseButton
                            element="a"
                            :href="`mailto:${page.contactEmail}`"
                        >
                            Contact our office
                            <BaseIcon icon="envelope" />
                        </BaseButton>
                    </template>
                    <template v-if="page.contactPhone">
                        <BaseButton
                            element="a"
                            :href="`tel:${page.contactPhone}`"
                        >
                            Contact our office
                            <BaseIcon icon="phone" />
                        </BaseButton>
                    </template>
                </BaseSection>

                <BaseSection
                    v-if="sectionEntries && sectionEntries.length"
                    class="section--wide section--extra-spacing"
                >
                    <template #title>
                        <h2>Sections</h2>
                    </template>
                    <BannerList :items="sectionEntries" />
                </BaseSection>

                <BaseSection
                    v-if="showOffices && page.offices.length"
                    class="section--wide section--extra-spacing"
                >
                    <template
                        v-if="showOfficeTitle"
                        #title
                    >
                        <h2>Working Groups</h2>
                    </template>
                    <BannerList :items="page.offices ?? []" />
                </BaseSection>

                <BaseSection
                    v-if="page.members && page.members.length"
                    class="section--wide section--extra-spacing"
                >
                    <template #title>
                        <h2>Board</h2>
                    </template>
                    <BaseMemberList :members="page.members" />
                </BaseSection>

                <BaseSection class="section--wide section--extra-spacing">
                    <template #title>
                        <h2>Related content</h2>
                    </template>
                    <template #action>
                        <nuxt-link
                            to="/news"
                            aria-label="See all news"
                        >
                            See all <BaseIcon icon="chevron-right" />
                        </nuxt-link>
                    </template>
                    <template #default>
                        <related-news :related-to-entry-id="page.id" />
                    </template>
                </BaseSection>
            </article>

            <article
                v-else-if="pending"
                class="bleed"
            >
                <BaseSection class="container-narrow">
                    <template #title>
                        <h1>
                            <ContentPlaceholdersHeading :lines="2" />
                        </h1>
                    </template>

                    <p class="lead">
                        <ContentPlaceholdersText :lines="5" />
                    </p>
                </BaseSection>

                <BaseSection class="section--wide section--extra-spacing">
                    <template #title>
                        <ContentPlaceholdersHeading :lines="2" />
                    </template>

                    <BaseMemberList
                        :members="[]"
                        :is-pending="true"
                    />
                </BaseSection>
            </article>
        </transition>
    </main>
</template>

<script setup>
import DetailQuery from '~/graphql/queries/office/Detail.graphql';

const { activeSite } = useMultisite();
const { variables } = useCraftGraphqlSlug();

const response = await useLazyAsyncQuery({
    query: DetailQuery,
    variables
});

const {
    craftEntry: page,
    headerImage,
    pending,
} = usePageQuery(
    response,
    { entryKey: 'page', activeSite }
);

const showOffices = computed(() => ['yua', 'yuo', 'yau'].includes(page.value?.officeType));
const showOfficeTitle = computed(() => ['yua', 'yau'].includes(page.value?.officeType));
const sectionEntries = computed(() => (page.value?.officeType === 'sections' ? page.value?.sectionEntries : []));
</script>
